import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, IconButton, Breadcrumbs, Link,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { axiosPartners } from "../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const SymbolBrokerList = () => {
  const { symbolName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const decodedSymbol = decodeURIComponent(symbolName);
  const symbolId = location.state?.symbolId;

  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosPartners.get(`/brokers/symbol-wise-broker-user-count/?symbol_id=${symbolId}`);
      setBrokers(res.data?.response ?? []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load brokers");
    } finally {
      setLoading(false);
    }
  }, [symbolId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBrokerClick = (broker) => {
    navigate(`../broker/${encodeURIComponent(broker.broker_id__broker_name)}/users`, {
      relative: "path",
      state: {
        symbolId,
        brokerId: broker.broker_id,
        symbolName: decodedSymbol,
        brokerName: broker.broker_id__broker_name,
      },
    });
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>

      {/* Header */}
      <Box sx={{ background: "#04343a", borderRadius: "12px", px: 3, py: 2, mb: 2, display: "flex", alignItems: "center", gap: 1.5, boxShadow: "0 4px 20px rgba(4,52,58,0.3)" }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#eef2f7", p: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <BusinessIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
        <Box>
          <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
            {decodedSymbol} — Brokers
          </Typography>
          <Typography sx={{ color: "#b8d4c4", fontSize: 12 }}>Click a broker to see its users</Typography>
        </Box>
      </Box>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" sx={{ fontSize: 12, color: "#016551", cursor: "pointer" }} onClick={() => navigate(-1)}>
          Symbolwise Dashboard
        </Link>
        <Typography sx={{ fontSize: 12, color: "#0f172a", fontWeight: 600 }}>{decodedSymbol}</Typography>
      </Breadcrumbs>

      {/* Table */}
      <Box sx={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ background: "#04343a", px: 3, py: 1.5 }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
            {brokers.length} Broker{brokers.length !== 1 ? "s" : ""} for {decodedSymbol}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#016551" }} size={36} />
          </Box>
        ) : brokers.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No brokers found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Broker Name", "User Count"].map((h) => (
                    <TableCell key={h} sx={{ background: "#04343a", color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", py: 1.5, whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {brokers.map((broker, i) => (
                  <TableRow
                    key={broker.broker_id}
                    onClick={() => handleBrokerClick(broker)}
                    sx={{ cursor: "pointer", "&:hover": { background: "#f0fdf4" }, transition: "background 0.15s" }}
                  >
                    <TableCell sx={{ fontSize: 12, color: "#94a3b8", py: 1.5, width: 50 }}>{i + 1}</TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#04343a" }}>
                        {broker.broker_id__broker_name}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          icon={<PeopleAltIcon sx={{ fontSize: "14px !important" }} />}
                          label={`${broker.user_count} User${broker.user_count !== 1 ? "s" : ""}`}
                          size="small"
                          sx={{ background: "#e0f2fe", color: "#0369a1", fontWeight: 700, fontSize: 12, height: 26, borderRadius: "6px" }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default SymbolBrokerList;
